import { Icon, mergeStyleSets } from '@fluentui/react';
import React from 'react';
import Store from '../shared/state/Store';
import UiReducer from '../shared/state/UiReducer';
import AppsApi from '../shared/util/AppsApi';
import EditableText from '../shared/EditableText';
import VerticalTripleDotMenu from '../shared/VerticalTripleDotMenu';
import User from '../shared/User';

const styles = mergeStyleSets({
  key: {
    display: 'inline-block',
    margin: '2px 0 2px 0',
  },

  assignedKey: {
    fontWeight: 600,
  },

  assignedIcon: {
    cursor: 'pointer',
  },

  hide: {
    display: 'none',
  },

  // noteCell: {
  //   maxWidth: '150px',
  // },

});

const defaultComponentStyles = {
  button: {
    outline: 'none !important',
    selectors: {
      i: {
        fontWeight: 'bold',
        fontSize: '15px',
        height: '15px',
      },
    },
  },
};

export default ({ keyData }) => {
  const [globalState, dispatch] = Store.useStore();
  const {
    key, expiry, policy, product, assignable, allowsCreators, disabled, disabledReason, size, assignments, granted, note, assigned,
    allowedActivations, totalActivations,
    // Note: totalActivations, totalDeactivations, and allowedDeactivations also exist in keyData
    // but are only used in LocalActivationsDialog, which extracts them directly
  } = keyData;

  const noteValue = note || '';

  const applyNoteValue = async (newValue) => {
    try {
      const responseJson = await AppsApi.updateKeyNote({ key, note: newValue });
      if (responseJson?.code >= 300 || responseJson?.error?.code >= 300) {
        dispatch(UiReducer.uiSetErrors(responseJson.messages));
        throw new Error(responseJson.messages?.[0] || 'Failed to update note');
      } else {
        // Get current account info and update the key in it
        const accountInfo = UiReducer.getGenericUiAttribute(globalState, UiReducer.Keys.AccountInfo);
        if (accountInfo) {
          const updatedAccountInfo = { ...accountInfo };

          // Update key in primaryAccount.keys if it exists there
          if (updatedAccountInfo.primaryAccount?.keys) {
            const keyIndex = updatedAccountInfo.primaryAccount.keys.findIndex((k) => k.key === key);
            if (keyIndex >= 0) {
              updatedAccountInfo.primaryAccount = {
                ...updatedAccountInfo.primaryAccount,
                keys: [...updatedAccountInfo.primaryAccount.keys],
              };
              updatedAccountInfo.primaryAccount.keys[keyIndex] = responseJson;
            }
          }

          // Update key in secondaryAccounts if it exists there
          if (updatedAccountInfo.secondaryAccounts) {
            updatedAccountInfo.secondaryAccounts = updatedAccountInfo.secondaryAccounts.map((secondaryAccount) => {
              const keyIndex = secondaryAccount.keys?.findIndex((k) => k.key === key);
              if (keyIndex >= 0) {
                const updatedSecondaryAccount = {
                  ...secondaryAccount,
                  keys: [...secondaryAccount.keys],
                };
                updatedSecondaryAccount.keys[keyIndex] = responseJson;
                return updatedSecondaryAccount;
              }
              return secondaryAccount;
            });
          }

          // Update the account info in the store
          dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.AccountInfo, updatedAccountInfo));
          dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.AccountInfoServerError, undefined));
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('Failed to update note');
      }
    }
  };

  return (
    <tr>
      {/* <td>v</td> */}
      <td><span className={[styles.key, (assigned || granted) ? styles.assignedKey : undefined].join(' ')}>{key}</span></td>
      <td>
        <Icon
          iconName="EditCreate"
          className={(!disabled && allowsCreators && (assigned || granted)) ? styles.assignedIcon : styles.hide}
          title="This key grants you Creator access"
        />
      </td>
      <td>{new Date(expiry).toLocaleDateString(navigator.languages[0], { dateStyle: 'medium' })}</td>
      <td>
        <VerticalTripleDotMenu
          menuItems={[
            {
              key: 'editCloud',
              text: 'Edit cloud assignments',
              onClick: () => {
                dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.AccountKeyAssignmentDialogOpen, keyData));
              },
            },
            ...(User.getEnabledFeatures(globalState).includes('activationControl') ? [{
              key: 'manageLocal',
              text: 'Manage local activations',
              onClick: () => {
                dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.LocalActivationsDialogOpen, keyData));
              },
            }] : []),
          ]}
          styles={defaultComponentStyles}
        />
      </td>
      <td>{`${granted ? '?' : assignments.length} / ${assignable ? size : 0}`}</td>
      <td>{`${totalActivations || 0} / ${allowedActivations || 0}`}</td>
      <td>{`${product}`.toLowerCase()}</td>
      <td>{policy}</td>
      <td>{disabledReason}</td>
      <td className={styles.noteCell}>
        <EditableText
          applyOnEnter={false}
          editableRows={5}
          actualValue={noteValue}
          placeholder="Enter note"
          applyValue={applyNoteValue}
          tooltip={noteValue || 'Click to edit note'}
          styles={{
            text: {
              maxWidth: '150px',
              width: 'fit-content',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            root: {
              width: 'fit-content',
            },
            icon: {
              fontSize: '50px !important',
            },
            input: {
              maxHeight: '100px',
              overflowY: 'auto',
            },
          }}
        />
      </td>
    </tr>
  );
};
